import {
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
} from "react";
import clsx from "clsx";
import { useCustomerSearch, type CustomerResult } from "../../lib/queries/customers";
import styles from "./CustomerCombobox.module.css";

export interface CustomerComboboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  label?: string;
  error?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  /** Called when the user selects an existing customer from the dropdown. */
  onSelectCustomer?: (customer: CustomerResult) => void;
}

/**
 * A name-input with customer typeahead. When the user types 2+ characters the
 * component searches for matching customers and shows a dropdown. Selecting a
 * customer fires onSelectCustomer so the parent can pre-fill phone/email.
 *
 * The component also works as a plain text input – the user can ignore the
 * dropdown and type a brand-new customer name.
 */
export function CustomerCombobox({
  label,
  error,
  hint,
  value,
  onChange,
  onSelectCustomer,
  ...rest
}: CustomerComboboxProps) {
  const id = useId();
  const listId = `${id}-list`;

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Debounced query: wait 300 ms after the user stops typing.
  const [debouncedQuery, setDebouncedQuery] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(value), 300);
    return () => clearTimeout(t);
  }, [value]);

  const { data: results = [] } = useCustomerSearch(debouncedQuery);

  // Open dropdown when results arrive (and input is focused).
  useEffect(() => {
    if (results.length > 0 && document.activeElement?.id === id) {
      setOpen(true);
      setActiveIdx(-1);
    } else if (results.length === 0) {
      setOpen(false);
    }
  }, [results, id]);

  // Close on outside click.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function handleSelect(c: CustomerResult) {
    onChange(c.name);
    onSelectCustomer?.(c);
    setOpen(false);
    setActiveIdx(-1);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  function contactLine(c: CustomerResult): string {
    return [c.phone, c.email].filter(Boolean).join(" · ");
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={styles.field}>
        {label && (
          <label className={styles.label} htmlFor={id}>
            {label}
          </label>
        )}
        <div className={clsx(styles.inputWrap, open && styles.inputWrapOpen, error && styles.hasError)}>
          <input
            id={id}
            className={styles.input}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (!open && e.target.value.trim().length >= 2) setOpen(true);
            }}
            onFocus={() => {
              if (results.length > 0) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={
              open && activeIdx >= 0
                ? `${listId}-opt-${activeIdx}`
                : undefined
            }
            autoComplete="off"
            {...rest}
          />
        </div>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        {!error && hint && <p className={styles.hint}>{hint}</p>}
      </div>

      {open && results.length > 0 && (
        <ul
          id={listId}
          className={styles.dropdown}
          role="listbox"
          aria-label="Nalezení zákazníci"
        >
          {results.map((c, i) => (
            <li key={c.id} role="none">
              <button
                id={`${listId}-opt-${i}`}
                type="button"
                role="option"
                aria-selected={i === activeIdx}
                className={styles.option}
                onMouseDown={(e) => {
                  // mousedown fires before blur so the input doesn't lose
                  // focus before we capture the selection.
                  e.preventDefault();
                  handleSelect(c);
                }}
              >
                <span className={styles.optionName}>{c.name}</span>
                {contactLine(c) && (
                  <span className={styles.optionContact}>{contactLine(c)}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
